import React from 'react';
import BrandLogo from './BrandLogo';
import './EmailMarketingPage.css';

const emlImages = {
  hero: 'https://raw.githubusercontent.com/hansrajparmarcom/sajagsadhana-photos/main/photos-BeyonDigital/Email-Marketing/Hero%20Image.png',
  commercial: 'https://raw.githubusercontent.com/hansrajparmarcom/sajagsadhana-photos/main/photos-BeyonDigital/Email-Marketing/The%20Commercial.png',
  dataDigest: 'https://raw.githubusercontent.com/hansrajparmarcom/sajagsadhana-photos/main/photos-BeyonDigital/Email-Marketing/The%20Data%20Digest.png',
  minimalist: 'https://raw.githubusercontent.com/hansrajparmarcom/sajagsadhana-photos/main/photos-BeyonDigital/Email-Marketing/The%20Minimilist%20Muse.png',
};

const marqueeItems = [
  'Lifecycle Marketing',
  'Behavioral Triggers',
  'Hyper-Segmentation',
  'A/B Multivariate Testing',
  'List Hygiene',
  'Zero-Party Data',
  'Dynamic Personalization',
  'Dark Mode Optimization',
];

const inboxLogicCards = [
  {
    id: '01',
    title: 'Lifecycle Architecture',
    description:
      'Create automated customer journeys from welcome to win-back with timing that feels personal.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
        <rect x="3" y="3" width="7" height="7" />
        <rect x="14" y="3" width="7" height="7" />
        <rect x="3" y="14" width="7" height="7" />
        <rect x="14" y="14" width="7" height="7" />
      </svg>
    ),
  },
  {
    id: '02',
    title: 'Editorial Email Design',
    description:
      'Design responsive, premium templates with clean hierarchy for readability and conversion.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
        <path d="M3 21l3.8-1 10.8-10.8a2.4 2.4 0 1 0-3.4-3.4L3.4 16.6 3 21z" />
      </svg>
    ),
  },
  {
    id: '03',
    title: 'Behavioral Segmentation',
    description:
      'Send highly relevant campaigns based on activity, interests, and purchase behavior.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
        <path d="M3 14h4l3-9 4 14 3-8h4" />
      </svg>
    ),
  },
  {
    id: '04',
    title: 'Conversion Copywriting',
    description:
      'Use persuasive subject lines and action-focused copy to increase click-through and repeat purchase.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
        <circle cx="12" cy="8" r="4" />
        <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
      </svg>
    ),
  },
];

const stylesCards = [
  {
    title: 'The Minimalist Muse',
    description:
      'High-fashion layouts with generous white space and serif typography for premium storytelling.',
    tag: 'AESTHETIC . BRAND-FIRST . HIGH-FASHION',
    image: emlImages.minimalist,
  },
  {
    title: 'The Commerce Heavy',
    description:
      'Bold blocks and clear pricing hierarchy with direct action points for revenue-first campaigns.',
    tag: 'CONVERSION . ROI . SALES',
    image: emlImages.commercial,
  },
  {
    title: 'The Data Digest',
    description:
      'Structured modular grid built for newsletter clarity, educational content, and trust.',
    tag: 'AUTHORITY . STRUCTURE . INFORMATIVE',
    image: emlImages.dataDigest,
  },
];

const philosophyCards = [
  {
    quote:
      'An email address is an invitation into a personal space. We design every campaign to add value first, then conversion.',
    title: 'On Inbox Trust',
  },
  {
    quote:
      'Owning your audience changes everything. Email gives your brand continuity beyond algorithm shifts.',
    title: 'On Owned Growth',
  },
  {
    quote:
      'Strong lifecycle systems turn one-time buyers into long-term relationships through relevance and timing.',
    title: 'On Retention Momentum',
  },
];

export default function EmailMarketingPage() {
  return (
    <div className="eml-page">
      <nav className="eml-nav">
        <BrandLogo className="eml-nav__logo" size="nav" />
        <ul className="eml-nav__links">
          <li>
            <a href="#work">Campaign Showcase</a>
          </li>
          <li>
            <a href="#services">Inbox Logic</a>
          </li>
          <li>
            <a href="#about">Our Story</a>
          </li>
        </ul>
        <a href="#contact" className="eml-nav__cta">
          Contact
        </a>
      </nav>

      <section className="eml-hero">
        <div className="eml-hero__visual">
          <img
            src={emlImages.hero}
            alt="Email marketing hero"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </div>
        <div className="eml-hero__content">
          <p className="eml-hero__eyebrow">Est. 2026 - London</p>
          <h1>
            Masters
            <br />
            <em>Of First Party</em>
            <br />
            Data
          </h1>
          <p>
            Your email list is an audience you own. We engineer lifecycle email systems
            that generate repeat revenue with consistent brand experience.
          </p>
          <div className="eml-hero__actions">
            <a href="#work" className="eml-btn eml-btn--dark">
              View Showcase Campaigns
            </a>
            <a href="#about" className="eml-btn eml-btn--ghost">
              Our Story ↗
            </a>
          </div>
          <div className="eml-proof">
            <div>
              <strong>38%</strong>
              <span>Average open rate lift</span>
            </div>
            <div>
              <strong>42x</strong>
              <span>Average ROI benchmark</span>
            </div>
            <div>
              <strong>&lt;1%</strong>
              <span>Unsubscribe benchmark</span>
            </div>
          </div>
        </div>
      </section>

      <section className="eml-marquee">
        <div className="eml-marquee__track">
          {[...marqueeItems, ...marqueeItems].map((item, index) => (
            <React.Fragment key={`${item}-${index}`}>
              <span>{item}</span>
              <span className="eml-marquee__dot">·</span>
            </React.Fragment>
          ))}
        </div>
      </section>

      <section className="eml-section" id="services">
        <header className="eml-section__header">
          <h2>
            The <em>Inbox Logic</em>
          </h2>
        </header>
        <div className="eml-grid eml-grid--services">
          {inboxLogicCards.map((card) => (
            <article className="eml-service-card" key={card.title}>
              <p className="eml-service-card__num">{card.id}</p>
              <div className="eml-service-card__icon">{card.icon}</div>
              <h3>{card.title}</h3>
              <p>{card.description}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="eml-section" id="work">
        <header className="eml-section__header eml-section__header--styles">
          <h2>
            The Grid
            <br />
            <em>Styles</em>
          </h2>
        </header>
        <div className="eml-grid eml-grid--styles">
          {stylesCards.map((card) => (
            <article className="eml-style-card" key={card.title}>
              <div className="eml-style-card__image">
                <img
                  src={card.image}
                  alt={card.title}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </div>
              <div className="eml-style-card__body">
                <h3>{card.title}</h3>
                <p>{card.description}</p>
                <span>{card.tag}</span>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="eml-section" id="about">
        <header className="eml-section__header eml-section__header--philosophy">
          <h2>
            Email Marketing
            <br />
            <em>Philosophy</em>
          </h2>
        </header>
        <div className="eml-grid eml-grid--philosophy">
          {philosophyCards.map((card) => (
            <blockquote key={card.title} className="eml-philosophy-card">
              <span className="eml-philosophy-card__mark">"</span>
              <p>{card.quote}</p>
              <cite>{card.title}</cite>
            </blockquote>
          ))}
        </div>
      </section>

      <section className="eml-cta" id="contact">
        <div className="eml-cta__inner">
          <h2>
            Is your email list
            <br />
            <em>leaving money on the table?</em>
          </h2>
          <a href="mailto:sdevarsh54@beyondigital.co.uk" className="eml-btn eml-btn--dark">
            Request an email audit
          </a>
        </div>
      </section>

      <footer className="eml-footer">
        <div className="eml-footer__top">
          <div className="eml-footer__brand">
            <BrandLogo size="footer" asLink={false} variant="dark" />
            <p>Redefining what email marketing can do for the modern enterprise.</p>
          </div>

          <div className="eml-footer__links">
            <div>
              <h4>Agency</h4>
              <a href="/">About</a>
              <a href="/">Work</a>
              <a href="/">Careers</a>
            </div>
            <div>
              <h4>Services</h4>
              <a href="/">Strategy</a>
              <a href="/">Content</a>
              <a href="/">Paid Social</a>
            </div>
            <div>
              <h4>Connect</h4>
              <a href="https://www.instagram.com/beyondigital.co/">Instagram</a>
              <a href="/">LinkedIn</a>
              <a href="/">Twitter/X</a>
            </div>
          </div>
        </div>
        <div className="eml-footer__bottom">London - India - Dubai</div>
      </footer>
    </div>
  );
}
